<script setup>
// Sarrdeh: "Leads" - every conversation the lead-scoring AI has scored (n8n data table "leads"),
// read through the n8n workflow "ai train (chatwoot)" (action "leads", administrators only).
import { computed, ref, onMounted } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';

const API = 'https://automation.sarrdeh-tech.com/webhook/ai-train';
const PLATFORMS = [
  { key: 'all', label: 'All' },
  { key: 'Facebook', label: 'Facebook', icon: 'i-woot-facebook' },
  { key: 'Instagram', label: 'Instagram', icon: 'i-woot-instagram' },
  { key: 'WhatsApp', label: 'WhatsApp', icon: 'i-woot-whatsapp' },
];
const PLATFORM_ICON = { Facebook: 'i-woot-facebook', Instagram: 'i-woot-instagram', WhatsApp: 'i-woot-whatsapp' };

const currentUser = useMapGetter('getCurrentUser');
const accountId = useMapGetter('getCurrentAccountId');

const leads = ref([]);
const serious = ref(60);
const isLoading = ref(true);
const loadError = ref('');
const platform = ref('all');
const minScore = ref('serious');
const search = ref('');
const sortBy = ref('recent');
const openId = ref(null);

const load = async () => {
  isLoading.value = true;
  loadError.value = '';
  try {
    const token = currentUser.value?.access_token;
    if (!token) throw new Error('Your user has no API access token.');
    const response = await fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'leads', token }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.ok) throw new Error(data.error || `Request failed (${response.status}).`);
    leads.value = data.leads || [];
    serious.value = data.serious || 60;
  } catch (e) {
    loadError.value = e.message;
  } finally {
    isLoading.value = false;
  }
};

const threshold = computed(() => {
  if (minScore.value === 'serious') return serious.value;
  if (minScore.value === 'warm') return 40;
  return 0;
});
const filtered = computed(() => {
  const q = search.value.trim().toLowerCase();
  const list = leads.value.filter(
    l =>
      (platform.value === 'all' || l.platform === platform.value) &&
      l.score >= threshold.value &&
      (!q || `${l.name} ${l.username} ${l.summary}`.toLowerCase().includes(q))
  );
  return sortBy.value === 'score'
    ? [...list].sort((a, b) => b.score - a.score || String(b.lastActivity).localeCompare(String(a.lastActivity)))
    : list;
});
const stats = computed(() => {
  const inPlatform = leads.value.filter(l => platform.value === 'all' || l.platform === platform.value);
  return {
    total: inPlatform.length,
    serious: inPlatform.filter(l => l.score >= serious.value).length,
    reported: inPlatform.filter(l => l.reported).length,
  };
});

const scoreClass = score => {
  if (score >= serious.value) return 'bg-n-ruby-3 text-n-ruby-11';
  if (score >= 40) return 'bg-n-amber-3 text-n-amber-11';
  return 'bg-n-alpha-2 text-n-slate-11';
};
const timeAgo = value => {
  const t = new Date(value).getTime();
  if (!t) return '';
  const minutes = Math.round((Date.now() - t) / 60000);
  if (minutes < 60) return `${Math.max(minutes, 1)} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return days < 30 ? `${days} d ago` : new Date(t).toLocaleDateString();
};
const chatLink = lead => `/app/accounts/${accountId.value}/conversations/${lead.conversationId}`;
const toggle = lead => {
  openId.value = openId.value === lead.id ? null : lead.id;
};

onMounted(load);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <header class="flex flex-wrap items-center justify-between gap-4 px-4 py-4 md:px-6 border-b border-n-weak">
      <div class="min-w-0">
        <h1 class="text-lg font-medium text-n-slate-12">Leads</h1>
        <p class="text-sm text-n-slate-11">
          Chats the AI scored after they went quiet for 30 minutes. {{ serious }}%+ is a serious lead and is reported on WhatsApp.
        </p>
      </div>
      <Button label="Reload" icon="i-lucide-refresh-cw" variant="ghost" color="slate" size="sm" :is-loading="isLoading" @click="load" />
    </header>

    <div class="flex flex-wrap items-center gap-3 px-4 py-3 md:px-6 border-b border-n-weak">
      <div class="flex items-center max-w-full gap-1 p-0.5 overflow-x-auto rounded-lg bg-n-alpha-1">
        <button
          v-for="p in PLATFORMS"
          :key="p.key"
          type="button"
          class="flex items-center gap-1.5 px-2.5 py-1 text-sm rounded-md whitespace-nowrap shrink-0"
          :class="platform === p.key ? 'bg-n-solid-1 text-n-slate-12 shadow-sm' : 'text-n-slate-11 hover:text-n-slate-12'"
          @click="platform = p.key"
        >
          <span v-if="p.icon" :class="p.icon" class="size-3.5" />
          {{ p.label }}
        </button>
      </div>
      <label class="relative flex items-center">
        <span class="sr-only">Minimum score</span>
        <select v-model="minScore" class="!w-auto !h-8 !mb-0 !py-0 pl-2.5 pr-8 text-sm border rounded-md appearance-none cursor-pointer border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-n-brand">
        <option value="serious">Serious ({{ serious }}%+)</option>
        <option value="warm">Warm (40%+)</option>
        <option value="all">All scores</option>
        </select>
        <span class="absolute pointer-events-none i-lucide-chevron-down size-4 right-2 text-n-slate-11" />
      </label>
      <label class="relative flex items-center">
        <span class="sr-only">Sort</span>
        <select v-model="sortBy" class="!w-auto !h-8 !mb-0 !py-0 pl-2.5 pr-8 text-sm border rounded-md appearance-none cursor-pointer border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-n-brand">
        <option value="recent">Newest first</option>
        <option value="score">Highest score first</option>
        </select>
        <span class="absolute pointer-events-none i-lucide-chevron-down size-4 right-2 text-n-slate-11" />
      </label>
      <input
        v-model="search"
        type="search"
        aria-label="Search leads"
        placeholder="Search name or summary"
        class="flex-1 min-w-[180px] !h-8 !mb-0 px-2.5 py-1 text-base md:text-sm border rounded-md border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-n-brand"
      />
      <span class="text-xs text-n-slate-10">
        {{ stats.total }} scored · {{ stats.serious }} serious · {{ stats.reported }} reported
      </span>
    </div>

    <div v-if="isLoading" class="flex items-center justify-center flex-1 text-sm text-n-slate-11">Loading leads...</div>
    <div v-else-if="loadError" class="flex flex-col items-center justify-center flex-1 gap-3 px-6 text-center">
      <span class="i-lucide-triangle-alert size-6 text-n-ruby-9" />
      <p class="text-sm text-n-slate-12">{{ loadError }}</p>
      <Button label="Try again" size="sm" @click="load" />
    </div>
    <div v-else-if="!filtered.length" class="flex flex-col items-center justify-center flex-1 gap-2 px-6 text-center">
      <span class="i-lucide-flame size-6 text-n-slate-9" />
      <p class="text-sm text-n-slate-11">
        No leads match these filters.<template v-if="minScore === 'serious'"> Try "Warm" or "All scores".</template>
      </p>
    </div>

    <ul v-else class="flex-1 pb-20 overflow-y-auto divide-y md:pb-0 divide-n-weak">
      <li v-for="lead in filtered" :key="lead.id" class="px-4 py-3 md:px-6 hover:bg-n-alpha-1">
        <div class="flex flex-wrap items-start gap-3 sm:flex-nowrap">
          <span class="shrink-0 w-12 py-1 text-sm font-semibold text-center rounded-lg tabular-nums" :class="scoreClass(lead.score)">
            {{ lead.score }}%
          </span>
          <div class="flex-1 min-w-0 basis-[calc(100%-3.75rem)] sm:basis-auto">
            <div class="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span dir="auto" class="text-sm font-medium text-n-slate-12">{{ lead.name || 'Unknown' }}</span>
              <span v-if="lead.username" class="text-xs text-n-slate-10">@{{ lead.username }}</span>
              <span class="flex items-center gap-1 text-xs text-n-slate-11">
                <span :class="PLATFORM_ICON[lead.platform]" class="size-3.5" />
                {{ lead.platform }} {{ lead.kind === 'comment' ? 'comment' : 'message' }}
              </span>
              <span v-if="lead.reported" class="text-xs text-n-teal-11" title="Reported on WhatsApp">✓ reported</span>
              <span class="text-xs text-n-slate-10">· {{ timeAgo(lead.lastActivity) }}</span>
            </div>
            <p dir="auto" class="mt-1 text-sm text-n-slate-11" :class="openId === lead.id ? '' : 'line-clamp-2'">{{ lead.summary }}</p>
            <pre
              v-if="openId === lead.id && lead.transcript"
              dir="auto"
              class="p-3 mt-2 overflow-x-auto font-sans text-xs whitespace-pre-wrap rounded-lg bg-n-alpha-1 text-n-slate-11 max-h-72"
            >{{ lead.transcript }}</pre>
          </div>
          <div class="flex items-center justify-end w-full gap-1 sm:w-auto shrink-0">
            <Button
              :label="openId === lead.id ? 'Less' : 'More'"
              variant="ghost"
              color="slate"
              size="xs"
              @click="toggle(lead)"
            />
            <router-link :to="chatLink(lead)">
              <Button label="Open chat" icon="i-lucide-message-square" variant="faded" color="slate" size="xs" />
            </router-link>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>
